import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card } from './Card'
import { useAuth } from '../lib/auth'

// Carrier e-mails about our disputes / claims (Sahin 2026-10-25, migration 080),
// read each morning from info@byelabel.com (incl. what Veysel forwards). The
// outcome shown is a SUGGESTION from wording rules; nothing changes a dispute by
// itself - the decision is recorded with the dispute's own buttons.

export interface CarrierReply {
  id: number
  received_at: string | null
  carrier: string | null
  from_address: string
  forwarded_by: string | null
  subject: string | null
  body_text: string | null
  attachments: string[] | null
  case_refs: string[] | null
  tracking_numbers: string[] | null
  suggested_outcome: 'credited' | 'rejected' | 'more_info' | 'acknowledged' | 'unclear' | 'submitted'
  direction: 'from_carrier' | 'to_carrier'   // to_carrier = our own e-mail to the carrier (migration 081)
  suggested_amount: string | null
  status: 'NEW' | 'REVIEWED' | 'IGNORED'
  reviewed_by: string | null
  reviewed_at: string | null
  disputes: { dispute_id: number; matched_by: string }[]
}

const OUTCOME: Record<CarrierReply['suggested_outcome'], { label: string; cls: string }> = {
  credited:     { label: 'Looks credited / approved', cls: 'pill pill-mint' },
  rejected:     { label: 'Looks rejected',            cls: 'pill pill-flame' },
  more_info:    { label: 'Carrier asks for info',     cls: 'pill pill-honey' },
  acknowledged: { label: 'Received / under review',   cls: 'pill pill-royal' },
  unclear:      { label: 'Read it',                   cls: 'pill pill-ink' },
  submitted:    { label: 'We wrote to the carrier',   cls: 'pill pill-ink' },
}
const MATCHED_BY: Record<string, string> = {
  thread: 'reply to our e-mail', case_ref: 'case number', tracking: 'tracking number', invoice: 'invoice number', manual: 'linked by hand',
}

function when(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—'
}

export function ReplyCard({ r, showLinks = true, invalidateKeys = [] }:
    { r: CarrierReply; showLinks?: boolean; invalidateKeys?: unknown[][] }) {
  const { can } = useAuth()
  const qc = useQueryClient()
  const [linkTo, setLinkTo] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['carrier-replies'] })
    qc.invalidateQueries({ queryKey: ['disputes'] })
    for (const k of invalidateKeys) qc.invalidateQueries({ queryKey: k })
  }
  const setStatus = useMutation({
    mutationFn: async (status: 'NEW' | 'REVIEWED' | 'IGNORED') => {
      const res = await fetch(`/api/carrier-replies/${r.id}/status`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }),
      })
      if (!res.ok) throw new Error((await res.json())?.error || 'Saving failed')
    },
    onSuccess: () => { setErr(null); refresh() },
    onError: (e: Error) => setErr(e.message),
  })
  const link = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/carrier-replies/${r.id}/link`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dispute_id: Number(linkTo.replace(/\D/g, '')) }),
      })
      if (!res.ok) throw new Error((await res.json())?.error || 'Linking failed')
    },
    onSuccess: () => { setLinkTo(''); setErr(null); refresh() },
    onError: (e: Error) => setErr(e.message),
  })
  const o = OUTCOME[r.suggested_outcome] ?? OUTCOME.unclear
  const canAct = can('resolve_disputes')

  return (
    <div className={`rounded-lg border p-3 ${r.status === 'NEW' ? 'border-royal-100 bg-white' : 'border-ink-100 bg-ink-50'}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`${o.cls} text-[10.5px]`}>{o.label}{r.suggested_amount ? ` · $${Number(r.suggested_amount).toFixed(2)}` : ''}</span>
        {r.carrier && <span className="text-[11px] font-semibold text-ink-600">{r.carrier}</span>}
        <span className="text-[11px] text-ink-400">{when(r.received_at)}</span>
        {r.status !== 'NEW' && (
          <span className="text-[10.5px] text-ink-400">{r.status === 'REVIEWED' ? 'reviewed' : 'ignored'}{r.reviewed_by ? ` by ${r.reviewed_by}` : ''}</span>
        )}
      </div>
      <div className="mt-1 text-[12.5px] font-medium text-ink-900 break-words">{r.subject || '(no subject)'}</div>
      <div className="text-[11px] text-ink-500 break-all">
        {r.direction === 'to_carrier' ? 'to' : 'from'} {r.from_address}{r.forwarded_by && <> · forwarded by {r.forwarded_by}</>}
        {r.case_refs?.length ? <> · case {r.case_refs.slice(0, 3).join(', ')}</> : null}
      </div>
      {showLinks && (
        <div className="text-[11px] text-ink-600 mt-1">
          {r.disputes?.length
            ? <>About dispute{r.disputes.length > 1 ? 's' : ''}: {r.disputes.map(d => `#${d.dispute_id} (${MATCHED_BY[d.matched_by] ?? d.matched_by})`).join(', ')}</>
            : <span className="text-honey-600">Not matched to a dispute</span>}
        </div>
      )}
      <details className="mt-1.5">
        <summary className="text-[11.5px] text-royal-600 cursor-pointer">Show the e-mail</summary>
        <pre className="mt-1.5 text-[11.5px] text-ink-700 whitespace-pre-wrap break-words font-sans bg-ink-50 rounded-md p-2 max-h-80 overflow-y-auto">{r.body_text || '(empty)'}</pre>
        {r.attachments?.length ? <div className="text-[11px] text-ink-500 mt-1">Attachments: {r.attachments.join(', ')}</div> : null}
      </details>
      {canAct && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {r.status === 'NEW' ? (
            <>
              <button onClick={() => setStatus.mutate('REVIEWED')} disabled={setStatus.isPending}
                      className="h-7 px-2.5 rounded-md text-[11.5px] font-medium bg-royal-500 text-white hover:bg-royal-600">Mark reviewed</button>
              <button onClick={() => setStatus.mutate('IGNORED')} disabled={setStatus.isPending}
                      className="h-7 px-2.5 rounded-md text-[11.5px] border border-ink-200 text-ink-600 hover:bg-ink-50">Ignore</button>
            </>
          ) : (
            <button onClick={() => setStatus.mutate('NEW')} disabled={setStatus.isPending}
                    className="text-[11.5px] text-ink-500 hover:text-ink-800">Back to review</button>
          )}
          {showLinks && (
            <span className="inline-flex items-center gap-1">
              <input value={linkTo} onChange={e => setLinkTo(e.target.value)} placeholder="dispute #"
                     className="h-7 w-24 px-2 text-[11.5px] border border-ink-200 rounded-md" />
              <button onClick={() => link.mutate()} disabled={!linkTo.trim() || link.isPending}
                      className="text-[11.5px] text-royal-600 font-medium disabled:opacity-40">Link</button>
            </span>
          )}
          {err && <span className="text-[11px] text-flame-600">{err}</span>}
        </div>
      )}
    </div>
  )
}

/** Carrier Recovery -> Carrier replies: every carrier e-mail found, matched or not. */
export function CarrierRepliesTab() {
  const [status, setStatus] = useState<'NEW' | 'REVIEWED' | 'IGNORED' | 'ALL'>('NEW')
  const [matched, setMatched] = useState<'' | 'yes' | 'no'>('')
  const [carrier, setCarrier] = useState('')
  const qs = new URLSearchParams({ status })
  if (matched) qs.set('matched', matched)
  if (carrier) qs.set('carrier', carrier)
  const { data, isLoading, error } = useQuery<{ replies: CarrierReply[]; counts: Record<string, number> }>({
    queryKey: ['carrier-replies', qs.toString()],
    queryFn: async () => {
      const res = await fetch(`/api/carrier-replies?${qs}`)
      if (!res.ok) throw new Error((await res.json().catch(() => null))?.error || `HTTP ${res.status}`)
      return res.json()
    },
  })
  const carriers = ['DHL', 'FEDEX', 'UPS', 'USPS', 'ASENDIA', 'EPOST', 'APC', 'INTELCOM']
  const sel = 'h-8 px-2 text-[12.5px] border border-ink-200 rounded-md bg-white'

  return (
    <Card className="p-4">
      <p className="text-[12.5px] text-ink-500 mb-3">
        Carrier e-mails about disputes and claims, read every morning from info@byelabel.com (including what is
        forwarded there). The label is only a suggestion from the wording - open the dispute to record the decision.
      </p>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex items-center bg-ink-100 rounded-lg p-0.5 text-[12.5px] font-medium">
          {(['NEW', 'REVIEWED', 'IGNORED', 'ALL'] as const).map(s => (
            <button key={s} onClick={() => setStatus(s)}
                    className={`px-3 py-1 rounded-md ${status === s ? 'bg-white shadow-sm' : 'text-ink-500'}`}>
              {s === 'NEW' ? 'To review' : s === 'REVIEWED' ? 'Reviewed' : s === 'IGNORED' ? 'Ignored' : 'All'}
              {s !== 'ALL' && data?.counts?.[s] != null && <span className="ml-1 text-ink-400">{data.counts[s]}</span>}
            </button>
          ))}
        </div>
        <select value={matched} onChange={e => setMatched(e.target.value as typeof matched)} className={sel}>
          <option value="">Matched or not</option>
          <option value="yes">Matched to a dispute</option>
          <option value="no">Not matched</option>
        </select>
        <select value={carrier} onChange={e => setCarrier(e.target.value)} className={sel}>
          <option value="">All carriers</option>
          {carriers.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      {isLoading ? <p className="text-[13px] text-ink-400">Loading…</p>
       : error ? <p className="text-[13px] text-flame-600">{(error as Error).message}</p>
       : !data?.replies.length ? <p className="text-[13px] text-ink-500">Nothing here.</p>
       : <div className="space-y-2">{data.replies.map(r => <ReplyCard key={r.id} r={r} />)}</div>}
    </Card>
  )
}

/** In the dispute panel: the carrier e-mails about this dispute. */
export function DisputeReplies({ disputeId }: { disputeId: number }) {
  const { data, error } = useQuery<{ replies: CarrierReply[] }>({
    queryKey: ['dispute-replies', disputeId],
    queryFn: async () => {
      const res = await fetch(`/api/disputes/${disputeId}/replies`)
      if (!res.ok) throw new Error((await res.json().catch(() => null))?.error || `HTTP ${res.status}`)
      return res.json()
    },
  })
  if (error) return <p className="text-[11.5px] text-flame-600">Carrier e-mails could not be loaded: {(error as Error).message}</p>
  if (!data?.replies?.length) return null
  return (
    <div>
      <div className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide mb-2">Carrier e-mails</div>
      <div className="space-y-2">
        {data.replies.map(r => <ReplyCard key={r.id} r={r} showLinks={false} invalidateKeys={[['dispute-replies', disputeId]]} />)}
      </div>
    </div>
  )
}
